<!--支持远程查询的输入框-->
<template>
  <CFormItem :prop="name" :field-value="value">
    <ElSelect
      v-model="value"
      class="cell-text-select"
      :disabled="disabled"
      filterable
      remote
      placeholder=" "
      :remote-method="remoteMethod"
      :loading="loading"
      @change="valueChange"
      @keydown.stop="noop"
      @click.stop="noop"
      @visible-change="visibleChange"
    >
      <EditableOptions
        :options-data="options"
        :editor="props.params.editor"
        :before-edit="noop"
        :reload="noop"
      />
    </ElSelect>
  </CFormItem>
</template>

<script lang="ts" setup>
import { ref } from 'vue';
import { ICellRendererParams } from 'ag-grid-community';
import { IdLabelList } from '../../core/types';
import EditableOptions from '../../common/EditableOptions.vue';
import { DropdownEditor } from '../../common/DropdownEditor';
import { isString, isFunction, noop } from 'lodash-es';

const props = defineProps<{
  params: ICellRendererParams<any> & {
    fieldName?: string; // 字段名

    /*编辑框是否禁用 */
    disabled?: boolean | string | ((ps: ICellRendererParams<any>) => boolean);

    callback: (
      query: string,
      params: ICellRendererParams<any>
    ) => Promise<IdLabelList> | Promise<string[]>;

    textField?: string; // 显示的文字字段

    editor?: DropdownEditor; // 下拉列表编辑器

    valueChange?: (
      params: ICellRendererParams<any>,
      newValue: any,
      oldValue: string
    ) => void;
  };
}>();

// 字段名
// eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
const name = props.params.fieldName || props.params.colDef?.field!;

// eslint-disable-next-line vue/no-setup-props-destructure
const paramsDisabled = props.params.disabled;
const disabled = isString(paramsDisabled)
  ? !!props.params.data[paramsDisabled]
  : isFunction(paramsDisabled)
  ? paramsDisabled(props.params)
  : paramsDisabled;

const value = ref(
  props.params.textField // 显示文本字段
    ? props.params.data[props.params.textField]
    : props.params.value
);

let cache: IdLabelList = [];
const options = ref<IdLabelList>([]);
const loading = ref(false);

let loaded = false;
function visibleChange(visible: boolean) {
  if (visible && !loaded) {
    remoteMethod('');
  }
}

function fixEmptyOptions(res: IdLabelList) {
  if (res.length == 0) {
    // 返回一条空记录, 否则按钮不显示
    (res as any).push({
      id: 0,
      label: ' ',
      disabled: true,
      hidden: true // EditableOptions 读取该参数
    });
  }

  return res;
}

// 调用远程接口查询列表
const remoteMethod = async (query: string) => {
  loading.value = true;

  try {
    let res = await props.params.callback(query, props.params);
    if (!res) {
      res = [];
    }

    // 最多100条
    res = res.length > 100 ? res.slice(0, 100) : res;
    // 将 string[] 转成IdLabelList
    if (res.length > 0 && isString(res[0])) {
      res = res.map((e) => ({ id: e, label: e })) as any;
    }

    if (props.params.textField) {
      cache = res as any;
    }

    options.value = fixEmptyOptions(res as any);
    loaded = true;
  } finally {
    loading.value = false;
  }
};

async function valueChange(val: any) {
  const old = props.params.value;
  try {
    await props.params.valueChange?.(props.params, val, old);

    if (props.params.textField) {
      // 查找文本, 更新rowData
      const label = cache.find((e) => e.id == val)?.label ?? '';
      cache = [];
      // eslint-disable-next-line vue/no-mutating-props
      props.params.data[props.params.textField] = label;
    }
    props.params.node.setDataValue(name, val, 'form'); // 'form'参数用于区分谁触发的
  } catch (e) {
    value.value = old;
    throw e;
  }
}
</script>

<style lang="scss">
.cell-text-select {
  width: unset !important;
  margin-left: 8px;
  margin-right: 8px;
  cursor: default;

  & .el-input.is-disabled {
    .el-input__wrapper {
      --el-disabled-bg-color: transparent;
      --el-disabled-text-color: var(--el-input-text-color);

      border-color: transparent !important;
      box-shadow: none !important;

      input {
        cursor: default;
      }
    }
  }

  .el-input__wrapper {
    border: 1px solid #dcdfe655;
    box-shadow: none;
  }

  .el-input__wrapper:hover,
  .el-input__wrapper.is-focus {
    border-color: #dcdfe6;
    box-shadow: none;
  }
}
</style>
